import { z } from "zod";
import { UuidSchema, TimestampsSchema } from "./common.js";
import { IconNameSchema } from "./icon.js";
// The segment filter language. Used at runtime by the SQL compiler (backend)
// and by the segment-builder UI (frontend). `value` is intentionally `any`
// because its shape depends on the field/op pair (string, number, array, or a
// TimeWindowSchedule).
// Stored as a smallint on the DB. Values are stable — appending new variants
// is safe (use the next integer); renumbering breaks existing rows.
export var SegmentType;
(function (SegmentType) {
    SegmentType[SegmentType["Prebuilt"] = 0] = "Prebuilt";
    SegmentType[SegmentType["SavedSegment"] = 1] = "SavedSegment";
    SegmentType[SegmentType["Lookalike"] = 2] = "Lookalike";
    SegmentType[SegmentType["AIGenerated"] = 3] = "AIGenerated";
})(SegmentType || (SegmentType = {}));
export const SEGMENT_TYPE_LABELS = {
    [SegmentType.Prebuilt]: "Prebuilt",
    [SegmentType.SavedSegment]: "Saved Audience",
    [SegmentType.Lookalike]: "Lookalike",
    [SegmentType.AIGenerated]: "AI generated",
};
export const SegmentTypeSchema = z.nativeEnum(SegmentType);
export const SEGMENT_TYPES = [
    SegmentType.Prebuilt,
    SegmentType.SavedSegment,
    SegmentType.Lookalike,
    SegmentType.AIGenerated,
];
export const SegmentOperatorSchema = z.enum([
    "eq",
    "neq",
    "in",
    "nin",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "startsWith",
    "endsWith",
    "regex",
    "arrayContains",
    "intersects",
    "between",
    "exists",
    "timeWindow",
]);
export const SegmentFieldSchema = z.enum([
    "deviceType",
    "channelId",
    "platform",
    "categories",
    "tags",
    "city",
    "region",
    "country",
    "gender",
    "ageGroup",
    "isPaid",
    "livestream",
    "sessionDuration",
    "adFrequency",
    "createdAt",
    "deviceId",
    "ua",
    "lat",
    "lon",
    "h",
    "w",
    "maxAdDuration",
    "minAdDuration",
]);
export const TimeWindowRuleSchema = z.object({
    days: z.union([z.string(), z.array(z.string())]),
    start: z.string(),
    end: z.string(),
});
export const TimeWindowScheduleSchema = z.object({
    rules: z.array(TimeWindowRuleSchema),
    tz: z.string().optional(),
});
export const ConditionSchema = z.object({
    field: z.string(),
    op: z.string(),
    value: z.any(),
    ci: z.boolean().optional(),
    disabled: z.boolean().optional(),
});
export const SegmentDefinitionSchema = z.object({
    all: z.array(ConditionSchema).optional(),
    any: z.array(ConditionSchema).optional(),
    none: z.array(ConditionSchema).optional(),
});
// Persisted shape (returned from the backend after a segment is created).
export const SegmentBaseSchema = z
    .object({
    uuid: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    filters: SegmentDefinitionSchema,
    userId: UuidSchema.nullable(),
    cpm: z.number(),
    // Bitmask: 1=dynamic, 2=overlay, 4=banner, 8=static; 15=all
    kind: z.number(),
    icon: IconNameSchema.nullable(),
    type: SegmentTypeSchema,
})
    .merge(TimestampsSchema);
export const CreateSegmentSchema = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    filters: SegmentDefinitionSchema,
    cpm: z.number().min(0).optional().default(0),
    kind: z.number().int().min(0).max(15).optional().default(15),
    icon: IconNameSchema.nullable().optional(),
    type: SegmentTypeSchema.optional().default(SegmentType.SavedSegment),
});
export const UpdateSegmentSchema = z.object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    filters: SegmentDefinitionSchema.optional(),
    cpm: z.number().min(0).optional(),
    kind: z.number().int().min(0).max(15).optional(),
    icon: IconNameSchema.nullable().optional(),
    type: SegmentTypeSchema.optional(),
});
export const SegmentResponseSchema = SegmentBaseSchema;
export const SegmentListResponseSchema = z.array(SegmentResponseSchema);
export const SegmentWithCountsSchema = SegmentBaseSchema.extend({
    vasts: z.number(),
    totalImpressions: z.number(),
});
export const SegmentStatisticsSchema = z.object({
    vasts: z.number(),
    totalImpressions: z.number(),
});
export const SegmentPredictRequestSchema = z.object({
    description: z.string().min(1),
});
export const SegmentPredictResponseSchema = z.object({
    data: z.object({
        name: z.string(),
        description: z.string(),
        filters: SegmentDefinitionSchema,
    }),
});
export const SegmentPreviewCountRequestSchema = z.object({
    filters: SegmentDefinitionSchema,
});
export const SegmentPreviewCountResponseSchema = z.object({
    count: z.number(),
});
//# sourceMappingURL=segment.js.map